import React from "react";
import PropTypes from "prop-types";
import { ThreeDots } from "react-loader-spinner";
import useThemeColors from "../../hooks/useThemeColors";
import { canDo } from "../../utils/routeAccess";

// @material-ui/core components
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

// @material-ui/icons
import { AddCircle, Check, Close, Edit, Delete } from "@material-ui/icons";

// core components
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";

const TagManagementDialog = ({
  // Dialog state
  tagManagementModalOpen,
  setTagManagementModalOpen,
  confirmDeleteTag,
  setConfirmDeleteTag,

  // Tag state
  tags,
  isAddingTag,
  setIsAddingTag,
  newTagName,
  setNewTagName,
  editingTag,
  setEditingTag,
  tagActionLoading,

  // User details
  userDetails,

  // Functions
  handleSaveNewTag,
  handleSaveEditedTag,
  handleDeleteTag,
  handleCancelEditing,
  notify,

  // Styles and theme
  classes,
  t,
}) => {
  const { clientColor } = useThemeColors();
  const tagManagementDialog = (
    <Dialog
      open={tagManagementModalOpen}
      onClose={() => setTagManagementModalOpen(false)}
      fullWidth
      maxWidth="sm"
    >
      <ModalHeader
        title={t("initiatives.manage_tags")}
        pills={
          tags.length
            ? [
                {
                  key: "tags",
                  label: `${tags.length} ${t("initiatives.tags")}`,
                },
              ]
            : []
        }
        onClose={() => setTagManagementModalOpen(false)}
        closeLabel={t("initiatives.close")}
        clientColor={clientColor}
      />
      <DialogContent style={{ padding: "15px 10px 15px" }}>
        <Typography
          variant="body2"
          style={{
            marginBottom: "15px",
            fontSize: "14px",
            lineHeight: "1.4",
            color: "#888",
            fontStyle: "italic",
            fontWeight: 300,
            backgroundColor: "#f5f5f5",
            padding: "8px",
            borderRadius: "5px",
          }}
        >
          {t("initiatives.tags_explanation")}
        </Typography>

        <div style={{ marginRight: "-10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "15px 0 5px",
            }}
          >
            <Typography
              variant="h6"
              className={classes.cardTitle}
              style={{
                fontSize: "12px",
                fontWeight: 300,
                color: "#777",
                marginTop: "8px",
                marginLeft: "8px",
              }}
            >
              {t("initiatives.list_of_tags")}
            </Typography>
            <div
              style={{
                marginTop: "-5px",
                marginRight: "13px",
              }}
            >
              <Button
                color="client"
                className={classes.cardButton}
                onClick={() => setIsAddingTag(true)}
                disabled={isAddingTag || !!editingTag || tagActionLoading}
              >
                <AddCircle className={classes.cardButtonIcon} />
              </Button>
            </div>
          </div>
          <List dense style={{ padding: 0 }}>
            {isAddingTag && (
              <ListItem
                className={classes.tagListItemEditing}
                style={{ padding: "1px 8px", minHeight: "32px" }}
              >
                <ListItemText
                  style={{ margin: "0px 0px 0px -6px", padding: 0 }}
                >
                  <TextField
                    variant="standard"
                    size="small"
                    fullWidth
                    autoFocus
                    client
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        newTagName.trim() &&
                        !tagActionLoading
                      ) {
                        // Check for duplicate names (case-insensitive)
                        const isDuplicate = tags.some(
                          (tag) =>
                            tag.name.toLowerCase() ===
                            newTagName.trim().toLowerCase(),
                        );
                        if (isDuplicate) {
                          notify(
                            "initiatives.tag_name_already_exists",
                            "error",
                          );
                          return;
                        }
                        handleSaveNewTag();
                      }
                    }}
                    style={{
                      fontSize: "14px",
                      padding: "2px 6px",
                      lineHeight: "1",
                      margin: 0,
                    }}
                    InputProps={{
                      style: { fontSize: "14px" },
                      classes: {
                        underline: classes.tagEditInputUnderline,
                      },
                    }}
                  />
                </ListItemText>
                <ListItemSecondaryAction>
                  <Tooltip title={t("initiatives.save")}>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => {
                        // Check for duplicate names (case-insensitive)
                        const isDuplicate = tags.some(
                          (tag) =>
                            tag.name.toLowerCase() ===
                            newTagName.trim().toLowerCase(),
                        );
                        if (isDuplicate) {
                          notify(
                            "initiatives.tag_name_already_exists",
                            "error",
                          );
                          return;
                        }
                        handleSaveNewTag();
                      }}
                      disabled={!newTagName.trim() || tagActionLoading}
                      style={{ padding: 4 }}
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                    >
                      {tagActionLoading ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            gap: "2px",
                          }}
                        >
                          <div
                            style={{
                              width: "3px",
                              height: "3px",
                              backgroundColor: "green",
                              borderRadius: "50%",
                              animation: "threeDots 1.4s infinite ease-in-out",
                            }}
                          />
                          <div
                            style={{
                              width: "3px",
                              height: "3px",
                              backgroundColor: "green",
                              borderRadius: "50%",
                              animation:
                                "threeDots 1.4s infinite ease-in-out 0.2s",
                            }}
                          />
                          <div
                            style={{
                              width: "3px",
                              height: "3px",
                              backgroundColor: "green",
                              borderRadius: "50%",
                              animation:
                                "threeDots 1.4s infinite ease-in-out 0.4s",
                            }}
                          />
                        </div>
                      ) : (
                        <Check style={{ color: "green", fontSize: "18px" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("initiatives.cancel")}>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => {
                        setIsAddingTag(false);
                        setNewTagName("");
                      }}
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      style={{ padding: 4 }}
                    >
                      <Close style={{ color: "red", fontSize: "18px" }} />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            )}

            {(() => {
              return tags.map((tag) => (
                <ListItem
                  key={tag.id}
                  className={classes.tagListItem}
                  style={{ padding: "1px 8px", minHeight: "32px" }}
                >
                  {editingTag && editingTag.id === tag.id ? (
                    <>
                      <ListItemText
                        style={{ margin: "0px 0px 0px -6px", padding: 0 }}
                      >
                        <TextField
                          variant="standard"
                          size="small"
                          fullWidth
                          autoFocus
                          value={editingTag.name || ""}
                          onChange={(e) => {
                            setEditingTag({
                              ...editingTag,
                              name: e.target.value,
                            });
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" &&
                              editingTag.name.trim() &&
                              !tagActionLoading
                            ) {
                              // Check for duplicate names (case-insensitive), excluding current tag
                              const isDuplicate = tags.some(
                                (tag) =>
                                  tag.id !== editingTag.id &&
                                  tag.name.toLowerCase() ===
                                    editingTag.name.trim().toLowerCase(),
                              );
                              if (isDuplicate) {
                                notify(
                                  "initiatives.tag_name_already_exists",
                                  "error",
                                );
                                return;
                              }
                              handleSaveEditedTag();
                            }
                          }}
                          style={{
                            fontSize: "14px",
                            padding: "2px 6px",
                            lineHeight: "1",
                            margin: 0,
                          }}
                          InputProps={{
                            style: { fontSize: "14px" },
                            classes: {
                              underline: classes.tagEditInputUnderline,
                            },
                          }}
                        />
                      </ListItemText>
                      <ListItemSecondaryAction>
                        <Tooltip title={t("initiatives.save")}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={handleSaveEditedTag}
                            disabled={
                              !editingTag.name.trim() || tagActionLoading
                            }
                            style={{ padding: 4 }}
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                          >
                            {tagActionLoading ? (
                              <ThreeDots
                                color={clientColor}
                                height={10}
                                width={10}
                              />
                            ) : (
                              <Check
                                style={{ color: "green", fontSize: "18px" }}
                              />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("initiatives.cancel")}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={handleCancelEditing}
                            style={{ padding: 4 }}
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                          >
                            <Close style={{ color: "red", fontSize: "18px" }} />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </>
                  ) : (
                    <>
                      <ListItemText
                        primary={tag.name}
                        style={{
                          margin: 0,
                          padding: 0,
                          "& .MuiListItemText-primary": {
                            fontSize: "13px",
                            lineHeight: "1.2",
                          },
                        }}
                      />
                      <ListItemSecondaryAction>
                        {canDo(userDetails, "governance.initiatives.edit", [
                          "IMG",
                          "CAD",
                          "DEV",
                        ]) && (
                          <>
                            <Tooltip title={t("initiatives.edit")}>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => {
                                  setEditingTag({
                                    id: tag.id,
                                    name: tag.name,
                                  });
                                }}
                                disabled={
                                  isAddingTag ||
                                  !!editingTag ||
                                  tagActionLoading
                                }
                                style={{ padding: 4 }}
                                className={classes.tagActionButton}
                                disableRipple
                                disableFocusRipple
                                disableTouchRipple
                              >
                                <Edit style={{ fontSize: "16px" }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("initiatives.delete")}>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => setConfirmDeleteTag(tag)}
                                disabled={
                                  isAddingTag ||
                                  !!editingTag ||
                                  tagActionLoading
                                }
                                style={{ padding: 4 }}
                                className={classes.tagActionButton}
                                disableRipple
                                disableFocusRipple
                                disableTouchRipple
                              >
                                <Delete style={{ fontSize: "16px" }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              ));
            })()}
          </List>
        </div>
      </DialogContent>
    </Dialog>
  );

  const deleteConfirmDialog = (
    <Dialog
      open={!!confirmDeleteTag}
      onClose={() => setConfirmDeleteTag(null)}
      maxWidth="xs"
      fullWidth
    >
      <ModalHeader
        title={t("initiatives.confirm_delete_tag_title")}
        pills={[
          {
            key: "irreversible",
            label: t("common.irreversible"),
            tone: "error",
            dot: true,
          },
        ]}
        onClose={() => setConfirmDeleteTag(null)}
        closeDisabled={tagActionLoading}
        closeLabel={t("initiatives.close")}
        clientColor={clientColor}
      />
      <DialogContent style={{ padding: "10px 12px 20px 12px" }}>
        <Typography
          style={{
            fontSize: "14px",
            lineHeight: "1.5",
            color: "#666",
            fontWeight: 400,
          }}
        >
          {t("initiatives.confirm_delete_tag_body", {
            tagName: confirmDeleteTag?.name,
          })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setConfirmDeleteTag(null)}
          color="system_gray"
          simple
        >
          {t("initiatives.cancel")}
        </Button>
        <Button
          onClick={handleDeleteTag}
          color="danger"
          simple
          disabled={tagActionLoading}
        >
          {tagActionLoading ? (
            <ThreeDots color={clientColor} height={10} width={10} />
          ) : (
            t("initiatives.confirm_delete")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {tagManagementDialog}
      {deleteConfirmDialog}
    </>
  );
};

TagManagementDialog.propTypes = {
  // Dialog state
  tagManagementModalOpen: PropTypes.bool.isRequired,
  setTagManagementModalOpen: PropTypes.func.isRequired,
  confirmDeleteTag: PropTypes.object,
  setConfirmDeleteTag: PropTypes.func.isRequired,

  // Tag state
  tags: PropTypes.array.isRequired,
  isAddingTag: PropTypes.bool.isRequired,
  setIsAddingTag: PropTypes.func.isRequired,
  newTagName: PropTypes.string.isRequired,
  setNewTagName: PropTypes.func.isRequired,
  editingTag: PropTypes.object,
  setEditingTag: PropTypes.func.isRequired,
  tagActionLoading: PropTypes.bool.isRequired,

  // User details
  userDetails: PropTypes.object.isRequired,

  // Functions
  handleSaveNewTag: PropTypes.func.isRequired,
  handleSaveEditedTag: PropTypes.func.isRequired,
  handleDeleteTag: PropTypes.func.isRequired,
  handleCancelEditing: PropTypes.func.isRequired,
  notify: PropTypes.func.isRequired,

  // Styles and theme
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default TagManagementDialog;
